import React, { ReactElement, useEffect, useState } from 'react'
import {
  Modal,
  StyleSheet,
  Animated,
  TouchableWithoutFeedback,
  View,
  ViewProps,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native'
import tinycolor from 'tinycolor2'

import { Color, Radius, Spacing } from '../../style'
import { Icon } from '../icon'

const styles = StyleSheet.create({
  container: {},
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tinycolor(Color.metalGrey).setAlpha(0.3).toHex8String(),
  },
  popup: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    borderRadius: Radius.L,
    backgroundColor: Color.simplyWhite,
    padding: Spacing.XXL,
    overflow: 'hidden',
  },
  close: {
    position: 'absolute',
    top: Spacing.M,
    right: Spacing.M,
  },
})

export interface Props extends ViewProps {
  visible?: boolean
  closeable?: boolean
  closeButton?: ReactElement | false
  onOpen?: () => void
  onClose?: () => void
}

const FeedbackPopUp = (props: Props) => {
  const { visible, closeable, closeButton, onOpen, onClose, style, children, ...otherProps } = props
  const [shown, setShown] = useState(false)
  const opacity = React.useRef(new Animated.Value(visible !== false ? 1 : 0)).current
  const { width, height } = useWindowDimensions()

  useEffect(() => {
    if (shown !== visible) show(!!visible)
  }, [visible])

  const show = (value: boolean) => {
    Animated.timing(opacity, {
      toValue: value ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start(() => {
      setShown(value)
      if (!value) {
        onClose && onClose()
      } else {
        onOpen && onOpen()
      }
    })
  }

  const backdropStyle = { opacity }
  const popupStyle = {
    opacity,
    transform: [
      {
        scale: opacity,
      },
    ],
  }
  const contentStyle = {
    maxHeight: height - 2 * Spacing.L,
    maxWidth: width - 2 * Spacing.L,
  }
  return (
    <Modal transparent style={styles.container} visible={visible !== false}>
      <TouchableWithoutFeedback onPress={() => closeable !== false && show(false)} testID="PopUp.Backdrop">
        <Animated.View style={[styles.backdrop, backdropStyle]} />
      </TouchableWithoutFeedback>
      <Animated.View style={[styles.popup, popupStyle]} testID="PopUp.Content" pointerEvents="box-none">
        <View style={[styles.content, contentStyle, style]} {...otherProps}>
          {children}
          {closeable !== false &&
            closeButton !== false &&
            (closeButton || (
              <TouchableOpacity onPress={() => show(false)} testID="PopUp.Close" style={styles.close}>
                <Icon name="close" color={Color.premiumBlack} />
              </TouchableOpacity>
            ))}
        </View>
      </Animated.View>
    </Modal>
  )
}

export default FeedbackPopUp
